import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Gauge,
  Globe,
  Info,
  LayoutList,
  Package,
  Pencil,
  Tags,
  Trash2,
  XCircle,
  ZoomIn,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog';
import { Separator } from '~/components/ui/separator';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { useProductCategoryDisplay } from '~/features/catalog-operations/hooks/use-product-category-display';
import { useProductDetail } from '~/features/explorer/hooks/use-products-catalog';
import {
  getProductField,
  getProductPrice,
  getProductStatus,
} from '~/features/explorer/utils/products-catalog';
import { cn } from '~/utils/cn';

type Product = Record<string, unknown>;
type DetailTab = 'overview' | 'quality' | 'issues' | 'countries';

interface DataIssue {
  check: string;
  dimension: string;
  field: string;
  message: string;
  severity: string;
}

interface SourceIssue {
  attribute: string;
  code: string;
  description: string;
  severity: string;
}

interface Destination {
  approvedCountries?: string[];
  approved_countries?: string[];
  disapprovedCountries?: string[];
  disapproved_countries?: string[];
  pendingCountries?: string[];
  pending_countries?: string[];
  reportingContext?: string;
  reporting_context?: string;
}

/** Soft semantic pill classes per issue/destination severity — design tokens
 *  only, so severities read the same everywhere and adapt to dark mode. */
const SEVERITY_STYLES: Record<string, string> = {
  critical: 'border-error/20 bg-error-50 text-error',
  important: 'border-warning/20 bg-warning-50 text-warning',
  optional: 'border-border bg-muted text-muted-foreground',
  DISAPPROVED: 'border-error/20 bg-error-50 text-error',
  DEMOTED: 'border-warning/20 bg-warning-50 text-warning',
  NOT_IMPACTED: 'border-border bg-muted text-muted-foreground',
};

/** Leading icon for a severity, tinted with the matching semantic token. */
function severityIcon(severity: string): ReactNode {
  if (severity === 'critical' || severity === 'DISAPPROVED')
    return <XCircle className="text-error mt-0.5 h-3.5 w-3.5 shrink-0" />;
  if (severity === 'important' || severity === 'DEMOTED')
    return <AlertTriangle className="text-warning mt-0.5 h-3.5 w-3.5 shrink-0" />;
  return <Info className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" />;
}

/** Safely coerces a loose (unknown) value to a display string. */
function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

/** First non-empty stringified value across the given keys, else null. */
function getField(product: Product, ...keys: string[]): string | null {
  for (const key of keys) {
    const s = toStr(product[key]);
    if (s !== '') return s;
  }
  return null;
}

/** Pulls the first array-valued key as a clean string list. */
function getStringArray(product: Product, ...keys: string[]): string[] {
  for (const key of keys) {
    const val = product[key];
    if (Array.isArray(val))
      return val.filter((v): v is string => typeof v === 'string' && v !== '');
  }
  return [];
}

/** Normalizes a field key for de-dup/denylist matching (lowercase, no separators). */
function normKey(key: string): string {
  return key.toLowerCase().replace(/[_-]/g, '');
}

/** Humanizes a raw field key into a Title-Case label (camel + snake aware). */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Normalized keys excluded from the "all attributes" list — surfaced elsewhere
 *  (title/images/description/quality/issues) or non-scalar/internal. */
const ATTR_DENYLIST = new Set(
  [
    'title',
    'name',
    'description',
    'image',
    'imagelink',
    'imageurl',
    'images',
    'optimizedimages',
    'additionalimagelinks',
    'thumbnail',
    'src',
    'url',
    'link',
    'producturl',
    'rawdata',
    'productdetails',
    'variants',
    'dataqualityscore',
    'dataqualitygrade',
    'completenessscore',
    'validityscore',
    'consistencyscore',
    'integrityscore',
    'uniquenessscore',
    'timelinessscore',
    'dataissues',
    'sourceissues',
    'sourceissuescount',
    'criticalissues',
    'importantissues',
    'optionalissues',
    'totalissues',
    'hasitemissues',
    'sourcedestinations',
    // Internal ids / scope / timestamps — noise as "attributes".
    'id',
    'connectionid',
    'syncid',
    'subsidiaryid',
    'tenantid',
    'revisionid',
    'createdat',
    'updatedat',
    'ingestiondate',
    'lastingestedat',
    'ingestedat',
  ].map((k) => k),
);

/** Gathers every gallery image URL from the many shapes connectors use
 *  (optimizedImages, imageLink, images, additionalImageLinks) — primary first,
 *  de-duplicated — so the thumbnail strip shows all of a product's images. */
function collectImages(p: Product, rawData?: Record<string, unknown>): string[] {
  const urls: string[] = [];
  const pushUrl = (v: unknown) => {
    if (typeof v === 'string' && v.startsWith('http')) urls.push(v);
  };
  const pushArray = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const it of arr) {
      if (typeof it === 'string') pushUrl(it);
      else if (it && typeof it === 'object') {
        const o = it as Record<string, unknown>;
        pushUrl(o.src ?? o.url);
      }
    }
  };
  pushArray(p.optimizedImages ?? p.optimized_images);
  pushUrl(p.imageLink ?? p.image_link ?? p.imageUrl ?? p.image_url ?? p.image);
  pushArray(p.images);
  pushArray(p.additionalImageLinks ?? p.additional_image_links);
  if (rawData) {
    pushArray(rawData.optimizedImages ?? rawData.optimized_images);
    pushArray(rawData.images);
    pushArray(rawData.additionalImageLinks ?? rawData.additional_image_links);
  }
  return [...new Set(urls)];
}

interface ProductDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string | null;
  subsidiaryId?: string | undefined;
  row: Product | null;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  /** 0-based position of the open row within the current page (for the "n / total"
   *  counter and prev/next). Omit to hide navigation. */
  index?: number;
  /** Total products in the result set (for the counter). */
  total?: number;
  /** Step to the previous / next product; disabled at the boundaries. */
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
}

/** One label/value row in the ATTRIBUTES list. */
function AttrRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border/50 grid grid-cols-[7.5rem_1fr] gap-3 border-b py-2.5 last:border-b-0">
      <span className="text-muted-foreground text-xs leading-5">{label}</span>
      <span className="text-foreground truncate text-sm leading-5 font-medium">{value}</span>
    </div>
  );
}

/**
 * Right-side product detail drawer. Fetches fresh detail (while open),
 * shallow-merges it over the clicked row, and presents a large image gallery
 * with thumbnails, identity + status, copy-id / open-in-source actions, and the
 * Overview / Quality / Issues / Countries tabs. Prev/next steps the current page.
 */
export function ProductDetailDrawer({
  open,
  onOpenChange,
  connectionId,
  subsidiaryId,
  row,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  index,
  total,
  onPrev,
  onNext,
  canPrev = false,
  canNext = false,
}: ProductDetailDrawerProps) {
  const { t } = useTranslation('explorer');
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [expandedOpen, setExpandedOpen] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);

  const externalId = row
    ? toStr(row.externalId) ||
      toStr(row.external_id) ||
      toStr(row.offerId) ||
      toStr(row.offer_id) ||
      toStr(row.id)
    : '';

  const detailQuery = useProductDetail(
    connectionId ?? '',
    externalId,
    subsidiaryId,
    open && !!connectionId && !!externalId,
  );

  // Fresh detail shallow-merged over the clicked row so the drawer shows the
  // richest data available without waiting for the fetch.
  const p: Product = useMemo(() => {
    if (!row) return {};
    const fetched = detailQuery.data;
    if (!fetched) return row;
    const inner = (
      fetched.data && typeof fetched.data === 'object' ? fetched.data : fetched
    ) as Record<string, unknown>;
    return { ...row, ...inner };
  }, [row, detailQuery.data]);

  // Reset the gallery/zoom/tab whenever the shown product changes.
  const [lastId, setLastId] = useState(externalId);
  if (externalId !== lastId) {
    setLastId(externalId);
    setActiveImageIdx(0);
    setExpandedOpen(false);
    setActiveTab('overview');
    setSeverityFilter(null);
  }

  const title = getProductField(p, 'title', 'name');
  const offerId = getField(p, 'externalId', 'external_id', 'offerId', 'offer_id', 'id') ?? '';
  const brand = getField(p, 'brand', 'vendor');
  const description = getField(p, 'description');
  const status = getProductStatus(p);
  const link = getField(p, 'link', 'url', 'productUrl', 'product_url');
  // Category may arrive as a numeric GPC id — resolve it to the readable name.
  const rawCategory = getField(
    p,
    'category',
    'productType',
    'product_type',
    'googleProductCategory',
    'google_product_category',
  );
  const { display: categoryDisplay } = useProductCategoryDisplay(rawCategory);

  const rawData = (p.rawData ?? p.raw_data) as Record<string, unknown> | undefined;
  const allImages = collectImages(p, rawData);
  const activeImage = allImages[activeImageIdx] ?? allImages[0];

  // ATTRIBUTES — curated known fields first (friendly labels + order), then
  // every other scalar field on the product, so nothing is hidden.
  const attributes = useMemo(() => {
    const rows: { label: string; value: string }[] = [];
    const consumed = new Set<string>();

    const pushCurated = (label: string, value: string | null, keys: string[]) => {
      keys.forEach((k) => consumed.add(normKey(k)));
      if (value != null && value !== '' && value !== '—') rows.push({ label, value });
    };

    pushCurated(t('products.attrBrand'), brand, ['brand', 'vendor']);
    pushCurated(t('products.attrCategory'), categoryDisplay || null, [
      'category',
      'productType',
      'product_type',
      'googleProductCategory',
      'google_product_category',
    ]);
    pushCurated(t('products.attrColor'), getField(p, 'color', 'colour'), ['color', 'colour']);
    pushCurated(t('products.attrAvailability'), getField(p, 'availability') ?? status, [
      'availability',
    ]);
    pushCurated(t('products.attrCondition'), getField(p, 'condition'), ['condition']);
    pushCurated(t('products.attrSeason'), getField(p, 'season'), ['season']);
    pushCurated(t('products.price'), getProductPrice(p), ['priceAmount', 'price']);
    pushCurated(t('products.attrSalePrice'), getField(p, 'salePrice', 'sale_price'), [
      'salePrice',
      'sale_price',
    ]);
    pushCurated(
      t('products.attrCurrency'),
      getField(p, 'priceCurrency', 'currency', 'currencyCode'),
      ['priceCurrency', 'currency', 'currencyCode'],
    );
    pushCurated(
      t('products.attrGtin'),
      getField(p, 'gtin') ?? getStringArray(p, 'gtins')[0] ?? null,
      ['gtin', 'gtins'],
    );
    pushCurated(t('products.attrProductId'), offerId || null, [
      'externalId',
      'external_id',
      'offerId',
      'offer_id',
      'id',
    ]);

    // Every remaining scalar field, humanized — "show all attributes".
    for (const [key, value] of Object.entries(p)) {
      const norm = normKey(key);
      if (consumed.has(norm) || ATTR_DENYLIST.has(norm)) continue;
      if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')
        continue;
      const str = String(value).trim();
      if (str === '' || str === '—') continue;
      consumed.add(norm);
      rows.push({ label: humanizeKey(key), value: str });
    }
    return rows;
  }, [p, brand, status, offerId, categoryDisplay, t]);

  const gtins = getStringArray(p, 'gtins', 'gtin');

  // Lily AI attribute groups
  interface ProductAttribute {
    attributeName?: string;
    attribute_name?: string;
    attributeValue?: string;
    attribute_value?: string;
  }
  const productDetails = (p.productDetails ??
    p.product_details ??
    rawData?.productDetails ??
    rawData?.product_details) as ProductAttribute[] | undefined;
  const lilyGroups = new Map<string, string[]>();
  if (Array.isArray(productDetails)) {
    for (const pd of productDetails) {
      const name = pd.attributeName ?? pd.attribute_name ?? '';
      const value = pd.attributeValue ?? pd.attribute_value ?? '';
      if (!name || !value) continue;
      const list = lilyGroups.get(name) ?? [];
      list.push(value);
      lilyGroups.set(name, list);
    }
  }

  // Quality dimensions
  const qualityScore = Number(p.dataQualityScore ?? p.data_quality_score ?? 0);
  const qualityGrade = (toStr(p.dataQualityGrade) || toStr(p.data_quality_grade)).toLowerCase();
  const dimensions = [
    {
      label: t('products.dimCompleteness'),
      value: Number(p.completenessScore ?? p.completeness_score ?? 0),
    },
    { label: t('products.dimValidity'), value: Number(p.validityScore ?? p.validity_score ?? 0) },
    {
      label: t('products.dimConsistency'),
      value: Number(p.consistencyScore ?? p.consistency_score ?? 0),
    },
    {
      label: t('products.dimIntegrity'),
      value: Number(p.integrityScore ?? p.integrity_score ?? 0),
    },
    {
      label: t('products.dimUniqueness'),
      value: Number(p.uniquenessScore ?? p.uniqueness_score ?? 0),
    },
    {
      label: t('products.dimTimeliness'),
      value: Number(p.timelinessScore ?? p.timeliness_score ?? 0),
    },
  ];
  const hasQualityDetail = detailQuery.isSuccess;

  const rawDataIssues = p.dataIssues ?? p.data_issues ?? [];
  const dataIssues = (Array.isArray(rawDataIssues) ? rawDataIssues : []) as DataIssue[];

  const rawSourceIssuesValue = p.sourceIssues ?? p.source_issues ?? [];
  const rawSourceIssues = (
    Array.isArray(rawSourceIssuesValue) ? rawSourceIssuesValue : []
  ) as SourceIssue[];
  const sourceIssues = rawSourceIssues.filter(
    (issue, idx, arr) =>
      arr.findIndex(
        (i) =>
          i.code === issue.code &&
          i.attribute === issue.attribute &&
          i.description === issue.description,
      ) === idx,
  );
  const sourceIssuesCount = Number(
    p.sourceIssuesCount ?? p.source_issues_count ?? rawSourceIssues.length,
  );

  const rawDestinations = p.sourceDestinations ?? p.source_destinations ?? [];
  const destinations = (Array.isArray(rawDestinations) ? rawDestinations : []) as Destination[];

  function copyId() {
    void navigator.clipboard.writeText(offerId);
    toast.success(t('products.idCopied'));
  }

  // Quality bars/donut use semantic tokens: green (good) → amber → red.
  function barColor(v: number) {
    if (v >= 80) return 'bg-success';
    if (v >= 50) return 'bg-warning';
    return 'bg-error';
  }
  const scoreColorVar =
    qualityScore >= 80 ? 'var(--success)' : qualityScore >= 50 ? 'var(--warning)' : 'var(--error)';

  const issuesTabCount = dataIssues.length + sourceIssues.length;

  if (!row) return null;

  const showNav = typeof index === 'number' && typeof total === 'number' && total > 0;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:w-[42rem] sm:max-w-[95vw]"
        >
          {/* Header — prev/next + counter + row actions (close button from SheetContent) */}
          <div className="flex shrink-0 items-center gap-2 border-b px-4 py-3 pr-12">
            {showNav && (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer"
                  disabled={!canPrev}
                  onClick={onPrev}
                  aria-label={t('products.prevProduct')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 cursor-pointer"
                  disabled={!canNext}
                  onClick={onNext}
                  aria-label={t('products.nextProduct')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-muted-foreground ml-1 text-sm tabular-nums">
                  {t('products.navPosition', { current: index + 1, total })}
                </span>
              </div>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              {detailQuery.isFetching && (
                <span className="text-muted-foreground animate-pulse text-[10px]">
                  {t('products.loading')}
                </span>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 cursor-pointer gap-1.5"
                  onClick={() => {
                    onOpenChange(false);
                    onEdit(p);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" /> {t('products.edit')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-error hover:text-error h-8 cursor-pointer gap-1.5"
                  onClick={() => {
                    onOpenChange(false);
                    onDelete(p);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t('products.delete')}
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Main image + zoom hint */}
            <div className="bg-muted relative">
              {activeImage ? (
                <button
                  type="button"
                  onClick={() => {
                    setExpandedOpen(true);
                  }}
                  className="block h-[26rem] w-full cursor-zoom-in overflow-hidden"
                  aria-label={t('products.clickToZoom')}
                >
                  <CachedImage
                    src={activeImage}
                    alt={title}
                    className="h-full w-full object-contain"
                  />
                </button>
              ) : (
                <div className="flex h-[26rem] w-full items-center justify-center">
                  <Package className="text-muted-foreground/30 h-12 w-12" aria-hidden="true" />
                </div>
              )}
              {activeImage && (
                <span className="text-muted-foreground bg-background/85 pointer-events-none absolute right-3 bottom-3 flex items-center gap-1 rounded-md px-2 py-1 text-xs backdrop-blur-sm">
                  <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('products.clickToZoom')}
                </span>
              )}
            </div>

            {/* Preview strip — click a preview to swap the main image */}
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto border-b p-3">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setActiveImageIdx(i);
                    }}
                    className={cn(
                      'bg-muted h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-lg border transition-all',
                      i === activeImageIdx
                        ? 'border-primary ring-primary ring-2'
                        : 'opacity-70 hover:opacity-100',
                    )}
                    aria-label={t('products.viewImage', { index: i + 1 })}
                  >
                    <CachedImage src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Identity + actions */}
            <div className="space-y-3 border-b px-5 py-4">
              <SheetTitle className="text-base leading-6 font-bold">{title}</SheetTitle>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {brand && <span className="text-muted-foreground">{brand}</span>}
                {status && status !== '—' && (
                  <Badge variant="outline" className="pointer-events-none capitalize">
                    {status}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {offerId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 cursor-pointer gap-1.5"
                    onClick={copyId}
                  >
                    <Copy className="h-3.5 w-3.5" /> {t('products.copyId')}
                  </Button>
                )}
                {link && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 cursor-pointer gap-1.5"
                    asChild
                  >
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" /> {t('products.openInSource')}
                    </a>
                  </Button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="px-4 pt-3 pb-2">
              <SegmentedTabs<DetailTab>
                value={activeTab}
                onChange={setActiveTab}
                ariaLabel={t('products.detailTabsLabel')}
                className="sm:max-w-none"
                options={[
                  {
                    value: 'overview',
                    ariaLabel: t('products.tabOverview'),
                    label: (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <LayoutList className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {t('products.tabOverview')}
                      </span>
                    ),
                  },
                  {
                    value: 'quality',
                    ariaLabel: t('products.tabQuality'),
                    label: (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <Gauge className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {t('products.tabQuality')}
                        {hasQualityDetail && qualityScore > 0 && (
                          <span className="bg-foreground/10 rounded-full px-1.5 text-[10px] font-semibold tabular-nums">
                            {qualityScore.toFixed(0)}
                          </span>
                        )}
                      </span>
                    ),
                  },
                  {
                    value: 'issues',
                    ariaLabel: t('products.tabIssues'),
                    label: (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {t('products.tabIssues')}
                        {issuesTabCount > 0 && (
                          <span className="bg-foreground/10 rounded-full px-1.5 text-[10px] font-semibold tabular-nums">
                            {issuesTabCount}
                          </span>
                        )}
                      </span>
                    ),
                  },
                  {
                    value: 'countries',
                    ariaLabel: t('products.tabCountries'),
                    label: (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {t('products.tabCountries')}
                      </span>
                    ),
                  },
                ]}
              />
            </div>

            <div className="px-5 py-4">
              {/* Overview — attributes + description + lily/gtins */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {description && (
                    <div>
                      <p className="mb-1 text-sm font-semibold">{t('products.description')}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-muted-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
                      {t('products.attributes')}
                    </p>
                    {detailQuery.isLoading && attributes.length === 0 ? (
                      <div className="space-y-2 pt-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <Skeleton key={i} className="h-6 w-full" />
                        ))}
                      </div>
                    ) : attributes.length > 0 ? (
                      <div>
                        {attributes.map((attr) => (
                          <AttrRow key={attr.label} label={attr.label} value={attr.value} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground py-4 text-sm">
                        {t('products.noOverview')}
                      </p>
                    )}
                  </div>

                  {gtins.length > 1 && (
                    <>
                      <Separator />
                      <div>
                        <p className="mb-2 text-sm font-semibold">{t('products.gtins')}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {gtins.map((g, i) => (
                            <Badge
                              key={i}
                              variant="outline"
                              className="border-tag-chip-border bg-tag-chip-bg text-tag-chip-text pointer-events-none font-mono text-[11px]"
                            >
                              {g}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {lilyGroups.size > 0 && (
                    <>
                      <Separator />
                      <div>
                        <div className="mb-2 flex items-center gap-1.5">
                          <Tags className="text-muted-foreground h-4 w-4" />
                          <p className="text-sm font-semibold">{t('products.lilyAttributes')}</p>
                          <Badge
                            variant="secondary"
                            className="pointer-events-none ml-auto text-[10px]"
                          >
                            {t('products.groups', { count: lilyGroups.size })}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          {Array.from(lilyGroups.entries()).map(([name, values]) => (
                            <div key={name} className="flex items-start gap-2">
                              <span className="text-muted-foreground w-28 shrink-0 truncate pt-0.5 text-xs">
                                {name}
                              </span>
                              <div className="flex flex-wrap gap-1">
                                {values.map((v, i) => (
                                  <Badge
                                    key={i}
                                    variant="secondary"
                                    className="pointer-events-none text-[11px] font-normal"
                                  >
                                    {v}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Quality */}
              {activeTab === 'quality' &&
                (detailQuery.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-32 w-full rounded-2xl" />
                    <Skeleton className="h-8 w-full rounded-full" />
                  </div>
                ) : !hasQualityDetail ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Info className="text-muted-foreground/40 mb-2 h-8 w-8" />
                    <p className="text-muted-foreground text-sm">{t('products.noQuality')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-surface-stat-card flex items-center gap-6 rounded-2xl p-5">
                      <div className="relative h-24 w-24 shrink-0">
                        <div
                          className="h-24 w-24 rounded-full"
                          style={{
                            background: `conic-gradient(${scoreColorVar} ${String(qualityScore)}%, var(--muted) ${String(qualityScore)}% 100%)`,
                          }}
                        />
                        <div className="bg-card absolute inset-[7px] flex flex-col items-center justify-center rounded-full shadow-sm">
                          <span className="text-xl leading-none font-bold tabular-nums">
                            {qualityScore.toFixed(0)}
                          </span>
                          <span className="text-muted-foreground mt-0.5 text-[10px] capitalize">
                            {qualityGrade}
                          </span>
                        </div>
                      </div>
                      <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2.5">
                        {dimensions.map(({ label, value }) => (
                          <div key={label} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground text-[11px]">{label}</span>
                              <span className="text-[11px] font-semibold tabular-nums">
                                {value.toFixed(0)}
                              </span>
                            </div>
                            <div className="bg-background/60 h-1.5 w-full overflow-hidden rounded-full">
                              <div
                                className={cn('h-full rounded-full', barColor(value))}
                                style={{ width: `${String(Math.min(100, value))}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {dataIssues.length > 0 && (
                      <div className="space-y-1.5">
                        {dataIssues.map((issue, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-2.5 rounded-lg border px-3 py-2"
                          >
                            {severityIcon(issue.severity)}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-mono text-xs font-medium">{issue.field}</span>
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    'pointer-events-none rounded-full px-1.5 py-0 text-[10px] capitalize',
                                    SEVERITY_STYLES[issue.severity] ?? '',
                                  )}
                                >
                                  {issue.severity}
                                </Badge>
                                <span className="text-muted-foreground text-[11px] capitalize">
                                  {issue.dimension}
                                </span>
                              </div>
                              <p className="text-muted-foreground mt-0.5 text-xs">
                                {issue.message}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

              {/* Issues (source) */}
              {activeTab === 'issues' &&
                (detailQuery.isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((n) => (
                      <Skeleton key={n} className="h-12 w-full rounded-lg" />
                    ))}
                  </div>
                ) : sourceIssues.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <CheckCircle2 className="text-success/60 mb-2 h-8 w-8" />
                    <p className="text-success text-sm font-medium">
                      {t('products.noSourceIssues')}
                    </p>
                  </div>
                ) : (
                  (() => {
                    const allSeverities = ['DISAPPROVED', 'DEMOTED', 'NOT_IMPACTED'] as const;
                    const countBySeverity = Object.fromEntries(
                      allSeverities.map((s) => [
                        s,
                        sourceIssues.filter((i) => i.severity === s).length,
                      ]),
                    );
                    const visibleSeverities = allSeverities.filter(
                      (s) => (countBySeverity[s] ?? 0) > 0,
                    );
                    const filtered = severityFilter
                      ? sourceIssues.filter((i) => i.severity === severityFilter)
                      : sourceIssues;

                    return (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold">{t('products.sourceIssues')}</p>
                          <span className="text-muted-foreground text-xs">
                            {t('products.totalAcrossDestinations', { count: sourceIssuesCount })}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setSeverityFilter(null);
                            }}
                            className={cn(
                              'h-auto cursor-pointer rounded-full px-3 py-1 text-xs font-medium',
                              severityFilter === null
                                ? 'border-foreground bg-foreground text-background hover:bg-foreground hover:text-background'
                                : 'text-muted-foreground border-border',
                            )}
                          >
                            {t('products.allCount', { count: sourceIssues.length })}
                          </Button>
                          {visibleSeverities.map((s) => (
                            <Button
                              key={s}
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setSeverityFilter(severityFilter === s ? null : s);
                              }}
                              className={cn(
                                'h-auto cursor-pointer rounded-full px-3 py-1 text-xs font-medium capitalize',
                                severityFilter === s
                                  ? SEVERITY_STYLES[s]
                                  : 'text-muted-foreground border-border',
                              )}
                            >
                              {s.replace(/_/g, ' ').toLowerCase()} ({countBySeverity[s]})
                            </Button>
                          ))}
                        </div>

                        <div className="space-y-1.5">
                          {filtered.map((issue, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2.5 rounded-lg border px-3 py-2"
                            >
                              {severityIcon(issue.severity)}
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-xs font-medium">{issue.description}</span>
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      'pointer-events-none rounded-full px-1.5 py-0 text-[10px]',
                                      SEVERITY_STYLES[issue.severity] ?? '',
                                    )}
                                  >
                                    {issue.severity}
                                  </Badge>
                                </div>
                                <div className="mt-0.5 flex items-center gap-2">
                                  {issue.attribute && (
                                    <span className="text-muted-foreground font-mono text-[11px]">
                                      {issue.attribute}
                                    </span>
                                  )}
                                  <span className="text-muted-foreground/60 text-[11px]">
                                    {issue.code}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()
                ))}

              {/* Countries */}
              {activeTab === 'countries' &&
                (detailQuery.isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((n) => (
                      <Skeleton key={n} className="h-14 w-full rounded-lg" />
                    ))}
                  </div>
                ) : destinations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Package className="text-muted-foreground/30 mb-2 h-8 w-8" />
                    <p className="text-muted-foreground text-sm">{t('products.noDestinations')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {destinations.map((dest, i) => {
                      const context = dest.reportingContext ?? dest.reporting_context ?? 'Unknown';
                      const approved = dest.approvedCountries ?? dest.approved_countries ?? [];
                      const disapproved =
                        dest.disapprovedCountries ?? dest.disapproved_countries ?? [];
                      const pending = dest.pendingCountries ?? dest.pending_countries ?? [];
                      const overallStatus =
                        disapproved.length > 0
                          ? 'disapproved'
                          : pending.length > 0
                            ? 'pending'
                            : approved.length > 0
                              ? 'approved'
                              : 'unknown';
                      const statusPill =
                        overallStatus === 'approved'
                          ? 'border-success/20 bg-success-50 text-success'
                          : overallStatus === 'disapproved'
                            ? 'border-error/20 bg-error-50 text-error'
                            : overallStatus === 'pending'
                              ? 'border-warning/20 bg-warning-50 text-warning'
                              : 'border-border bg-muted text-muted-foreground';
                      return (
                        <div key={i} className="space-y-2 rounded-lg border px-3 py-2.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium capitalize">
                              {context.replace(/_/g, ' ').toLowerCase()}
                            </span>
                            <Badge
                              variant="outline"
                              className={cn(
                                'pointer-events-none rounded-full px-2 py-0 text-[10px] capitalize',
                                statusPill,
                              )}
                            >
                              {overallStatus}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-3 text-[11px]">
                            {approved.length > 0 && (
                              <span className="text-success">
                                {t('products.approved')}: {approved.join(', ')}
                              </span>
                            )}
                            {disapproved.length > 0 && (
                              <span className="text-error">
                                {t('products.disapproved')}: {disapproved.join(', ')}
                              </span>
                            )}
                            {pending.length > 0 && (
                              <span className="text-warning">
                                {t('products.pending')}: {pending.join(', ')}
                              </span>
                            )}
                            {approved.length === 0 &&
                              disapproved.length === 0 &&
                              pending.length === 0 && (
                                <span className="text-muted-foreground">
                                  {t('products.noCountryData')}
                                </span>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Expanded image lightbox — opened from the main image or a preview */}
      <Dialog open={expandedOpen} onOpenChange={setExpandedOpen}>
        <DialogContent className="max-w-5xl border-0 bg-transparent p-0 shadow-none sm:max-w-5xl">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <div className="relative flex items-center justify-center">
            {activeImage && (
              <CachedImage
                src={activeImage}
                alt={title}
                className="max-h-[85vh] w-auto rounded-lg object-contain"
              />
            )}
            {allImages.length > 1 && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="bg-background/80 hover:bg-background absolute top-1/2 left-3 h-9 w-9 -translate-y-1/2 cursor-pointer rounded-full shadow-sm"
                  onClick={() => {
                    setActiveImageIdx((i) => (i - 1 + allImages.length) % allImages.length);
                  }}
                  aria-label={t('products.prevImage')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="bg-background/80 hover:bg-background absolute top-1/2 right-3 h-9 w-9 -translate-y-1/2 cursor-pointer rounded-full shadow-sm"
                  onClick={() => {
                    setActiveImageIdx((i) => (i + 1) % allImages.length);
                  }}
                  aria-label={t('products.nextImage')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="bg-foreground/70 pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md px-2 py-0.5 text-xs font-medium text-white tabular-nums">
                  {t('products.navPosition', {
                    current: activeImageIdx + 1,
                    total: allImages.length,
                  })}
                </span>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
